import { supabase } from '@/integrations/supabase/client';
import { OvertimeRequest, OvertimeRequestInsert, OvertimeRequestUpdate } from '@/types/supabase-types'
import { OvertimeStatus } from '@/types/enums';
;

// We don't need to extend OvertimeRequest since it already has all the fields we need
type OvertimeHourRecord = OvertimeRequest;

export async function fetchEmployeeOvertimeHours(employeeId: string) {
  const { data, error } = await supabase
    .from('overtime_requests')
    .select('*')
    .eq('employee_id', employeeId)
    .order('date', { ascending: false });

  if (error) throw error;
  return data as OvertimeHourRecord[];
}

export async function submitOvertimeRequest(request: OvertimeRequestInsert) {
  const { data, error } = await supabase
    .from('overtime_requests')
    .insert(request as any)
    .select()
    .single();

  if (error) throw error;
  return data as OvertimeHourRecord;
}

export async function updateOvertimeRequest(id: string, updates: OvertimeRequestUpdate) {
  const { data, error } = await (supabase as any)
    .from('overtime_requests')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data as OvertimeHourRecord;
}

export async function deleteOvertimeRequest(id: string) {
  const { error } = await supabase
    .from('overtime_requests')
    .delete()
    .eq('id', id);

  if (error) throw error;
}

export async function addOvertimeHours(record: OvertimeHourRecord) {
  try {
    // Get the current user's session to determine if they are a department head
    const { data: sessionData } = await supabase.auth.getSession();
    const userId = sessionData.session?.user.id;
    
    // Default to 'pending' status if not provided
    const status = record.status || OvertimeStatus.PENDING;
    
    const { data, error } = await supabase
      .from('overtime_requests')
      .insert({
        ...record,
        status: status,
        submitted_at: new Date().toISOString(),
        // Store the current user as approver_id if they are submitting for someone else
        approver_id: record.employee_id !== userId ? userId : null
      } as any)
      .select() as { data: OvertimeRequest[] | null, error: unknown };
      
    if (error) throw error;
    
    return { success: true, data };
  } catch (error) {
    return { success: false, error };
  }
}

export async function approveOvertimeHours(id: string, approverId?: string, approverName?: string) {
  try {
    const { data, error } = await (supabase as any)
      .from('overtime_requests')
      .update({
        status: OvertimeStatus.APPROVED,
        updated_at: new Date().toISOString(),
        approver_id: approverId,
        approver_name: approverName
      })
      .eq('id', id)
      .select() as { data: OvertimeRequest[] | null, error: unknown };
      
    if (error) throw error;
    
    return { success: true, data };
  } catch (error) {
    return { success: false, error };
  }
}

export async function rejectOvertimeHours(id: string, approverId?: string, approverName?: string, reason?: string) {
  try {
    const { data, error } = await (supabase as any)
      .from('overtime_requests')
      .update({
        status: OvertimeStatus.REJECTED,
        updated_at: new Date().toISOString(),
        approver_id: approverId,
        approver_name: approverName,
        approver_comment: reason
      })
      .eq('id', id)
      .select() as { data: OvertimeRequest[] | null, error: unknown };
      
    if (error) throw error;
    
    return { success: true, data };
  } catch (error) {
    return { success: false, error };
  }
}

export async function calculateTotalOvertimeHours(employeeId: string, startDate?: string, endDate?: string) {
  try {
    let query = supabase
      .from('overtime_requests')
      .select('hours')
      .eq('employee_id', employeeId)
      .eq('status', OvertimeStatus.APPROVED); // Only count approved overtime
      
    if (startDate) {
      query = query.gte('date', startDate);
    }
    
    if (endDate) {
      query = query.lte('date', endDate);
    }
    
    const { data, error } = await query;
    
    if (error) throw error;
    
    const totalHours = Array.isArray(data) ? data.reduce((sum, record: any) => sum + parseFloat(record.hours.toString()), 0) : 0;
    return totalHours;
  } catch {
    return 0;
  }
}

/**
 * Get total approved overtime hours for an employee on a specific date
 * @param employeeId - Employee ID
 * @param date - Date in YYYY-MM-DD format
 * @returns Promise<number> - Total approved hours for that date
 */
export async function getApprovedOvertimeHoursForDate(employeeId: string, date: string): Promise<number> {
  try {
    const { data, error } = await supabase
      .from('overtime_requests')
      .select('hours')
      .eq('employee_id', employeeId)
      .eq('date', date)
      .eq('status', OvertimeStatus.APPROVED);
    
    if (error) throw error;
    
    const totalHours = Array.isArray(data) ? data.reduce((sum, record: any) => sum + parseFloat(record.hours.toString()), 0) : 0;
    return totalHours;
  } catch {
    return 0;
  }
}
